import { Button, ButtonVariants } from '@components/button/Button';
import cx from 'classnames';
import { ControlledFormCurrencyInput } from '@components/formInputs/ControlledFormCurrencyInput';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { IconButton } from '@components/iconButton/IconButton';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import { useMemo, useRef } from 'react';
import type { Control, FieldPath, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { RestaurantEvent } from '@shared/types/restaurants';
import {
  isBlankAddOnRow,
  type EventAddOnFormItem,
  type EventFormData,
  type TicketTierFormItem,
} from '../RestaurantEventForm';
import { AddonImageField } from './AddonImageField';
import styles from './AddonForm.scss';
import {
  getTierSelectionLabel,
  TierSelectionPopover,
} from './TierSelectionPopover';

interface AddonFormProps {
  control: Control<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
  index: number;
  restaurantId: string;
  ticketTiers?: TicketTierFormItem[];
  savedEventTiers?: RestaurantEvent['tiers'] | null;
  /** Card opened via Edit: Done returns to card view. */
  onDoneCardEdit?: () => void;
  /** New add-on (no id): Done returns to card view when the row is complete enough to show as a card. */
  onDoneNewAddOn?: () => void;
  /** When this is the only add-on row, resets fields to empty defaults (shown instead of delete). */
  onClearRow?: () => void;
  /** When set with `removeTooltipId`, shows delete control for this row (multiple rows). */
  onRemove?: () => void;
  /** Required when `onRemove` is set; used for the tooltip anchor id. */
  removeTooltipId?: string;
}

export const AddonForm = ({
  control,
  setValue,
  index,
  restaurantId,
  ticketTiers = [],
  savedEventTiers,
  onDoneCardEdit,
  onDoneNewAddOn,
  onClearRow,
  onRemove,
  removeTooltipId,
}: AddonFormProps) => {
  const nameField = `addOns.${index}.name` as FieldPath<EventFormData>;
  const priceField = `addOns.${index}.price` as FieldPath<EventFormData>;
  const descriptionField =
    `addOns.${index}.description` as FieldPath<EventFormData>;
  const imageKeyField = `addOns.${index}.imageKey` as FieldPath<EventFormData>;
  const tierIdsField = `addOns.${index}.tierIds` as FieldPath<EventFormData>;
  const addOnRowPath = `addOns.${index}` as FieldPath<EventFormData>;

  const addOnRow = useWatch({ control, name: addOnRowPath }) as
    | EventAddOnFormItem
    | undefined;

  const selectedTierIds = (addOnRow?.tierIds ?? []) as string[];

  const areRequiredFieldsFilled = useMemo(() => {
    const imageOk = String(addOnRow?.imageKey ?? '').trim() !== '';
    const nameOk = String(addOnRow?.name ?? '').trim() !== '';
    const rawPrice = addOnRow?.price;
    const priceOk = (() => {
      if (rawPrice === '' || rawPrice === null || rawPrice === undefined) {
        return false;
      }
      if (typeof rawPrice === 'number') return Number.isFinite(rawPrice);
      const priceString = String(rawPrice).trim();
      if (priceString === '') return false;
      return Number.isFinite(Number.parseFloat(priceString));
    })();
    return imageOk && nameOk && priceOk;
  }, [addOnRow?.imageKey, addOnRow?.name, addOnRow?.price]);

  const doneButtonDisabled = !areRequiredFieldsFilled;

  const handleDoneClick = () => {
    if (!areRequiredFieldsFilled) return;
    onDoneNewAddOn?.();
    onDoneCardEdit?.();
  };

  const photoMenuLayoutRef = useRef<HTMLDivElement>(null);
  const clearTooltipId = useHtmlId('addon-form-clear');

  const showRemoveAddonTopRight =
    onClearRow != null || (onRemove != null && removeTooltipId != null);

  const handleTierIdsChange = (tierIds: string[]) => {
    setValue(tierIdsField, tierIds, { shouldDirty: true });
  };

  return (
    <div ref={photoMenuLayoutRef}>
      <div className={styles.addonCard}>
        {showRemoveAddonTopRight ? (
          <div className={styles.addonFormTopRightAction}>
            {onClearRow != null ? (
              <IconButton
                key="addon-form-action-clear"
                ariaLabel="Clear"
                className={cx(
                  styles.eventModalActionIconButton,
                  styles.iconButtonDefaultStroke,
                )}
                dataTooltipId={clearTooltipId}
                disabled={isBlankAddOnRow(addOnRow)}
                iconName="rotateCcw"
                onClick={onClearRow}
                tooltipContent="Clear"
              />
            ) : onRemove != null && removeTooltipId != null ? (
              <IconButton
                key={`addon-form-action-delete-${removeTooltipId}`}
                ariaLabel="Delete"
                className={cx(
                  styles.eventModalActionIconButton,
                  styles.iconButtonDefaultStroke,
                )}
                dataTooltipId={removeTooltipId}
                iconName="trash"
                onClick={onRemove}
                tooltipContent="Delete"
              />
            ) : null}
          </div>
        ) : null}
        <div className={styles.addonCardContent}>
          <AddonImageField
            control={control}
            index={index}
            imageKeyField={imageKeyField}
            restaurantId={restaurantId}
            setValue={setValue}
            layoutRef={photoMenuLayoutRef}
          />

          <div className={styles.formFields}>
            <div className={styles.formRow}>
              <ControlledFormInput<EventFormData>
                control={control}
                name={nameField}
                label="Add-On Title"
                type="text"
                rules={{
                  validate: (value) => {
                    if (isBlankAddOnRow(addOnRow)) return true;
                    if (!String(value ?? '').trim()) return 'Title is required';
                    return true;
                  },
                }}
                variant={LayoutVariant.Vertical}
                labelClassName={styles.fieldLabel}
                darkMode
              />

              <ControlledFormCurrencyInput<EventFormData>
                control={control}
                name={priceField}
                label="Price ($)"
                rules={{
                  validate: (value) => {
                    if (isBlankAddOnRow(addOnRow)) return true;
                    const priceString = String(value ?? '').trim();
                    if (priceString === '') return 'Price is required';
                    return (
                      Number.isFinite(Number.parseFloat(priceString)) ||
                      'Enter a valid price'
                    );
                  },
                }}
                variant={LayoutVariant.Vertical}
                labelClassName={styles.fieldLabel}
                darkMode
              />
            </div>

            <div className={styles.descriptionField}>
              <ControlledFormTextareaAutosize<EventFormData>
                control={control}
                name={descriptionField}
                label="Description (optional)"
                placeholder="Enter a description for this add-on..."
                minRows={1}
                maxRows={2}
                variant={LayoutVariant.Vertical}
                labelClassName={styles.fieldLabel}
                ariaLabel="Add-on description"
                darkMode
              />
            </div>

            <div className={styles.formRowSecondary}>
              <div className={styles.formRowSecondaryMain}>
                {ticketTiers.length > 0 && (
                  <TierSelectionPopover
                    layoutRef={photoMenuLayoutRef}
                    ticketTiers={ticketTiers}
                    savedEventTiers={savedEventTiers}
                    selectedTierIds={selectedTierIds}
                    onTierIdsChange={handleTierIdsChange}
                    trigger={() => (
                      <Button
                        type="button"
                        label={getTierSelectionLabel(selectedTierIds)}
                        variant={ButtonVariants.Tertiary}
                        className={styles.actionButton}
                        onClick={() => {}}
                      />
                    )}
                  />
                )}
              </div>
              <Button
                type="button"
                label="Done"
                variant={ButtonVariants.Secondary}
                className={styles.doneEditingButton}
                isDisabled={doneButtonDisabled}
                onClick={handleDoneClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
